import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { uploadFromUri, sanitizeFilename } from '@/utils/FileManager';
import Toast from 'react-native-toast-message';
import ErrorHandler from '@/utils/ErrorHandler';
import { getCurrentOrgID } from '@/data/organization';
import { supabase } from '@/data/client';
import { getOrgStorageUsage, STORAGE_LIMIT_BYTES } from '@/data/files';

/**
 * Hook for managing file upload queue with progress tracking
 * 
 * @param {Object} params
 * @param {string} params.scope - 'resources' | 'event' | 'person'
 * @param {string} params.ownerID - eventInstanceID or personID (null for resources)
 * @param {string} params.folderID - current folder ID
 * 
 * @returns {Object} Upload queue state and actions
 */
// Each in-flight upload holds the file's bytes in JS heap — cap parallelism
// so multi-file uploads can't OOM the app.
const MAX_PARALLEL_UPLOADS = 2;

export const useFileUploads = ({ scope, ownerID, folderID }) => {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState([]);

  // Refs for values needed inside callbacks/timeouts without stale closures.
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const waitingUploadsRef = useRef([]);
  const activeUploadsRef = useRef(0);
  const cleanupTimerRef = useRef(null);
  // Ids already sent to createFileMutation — dedupes repeat triggers and
  // StrictMode double-invocation of the completion effect.
  const recordedIdsRef = useRef(new Set());
  
  // Mutation to create file records in the database
  const createFileMutation = useMutation({
    mutationFn: async ({ files }) => {
      const orgID = await getCurrentOrgID();
      const { data: { user } } = await supabase.auth.getUser();

      const { data: regUser } = await supabase
        .from('RegisteredUsersOrganizations')
        .select('personID')
        .eq('userID', user.id)
        .eq('organizationID', orgID)
        .maybeSingle();

      if (!regUser?.personID) {
        throw new Error('Could not find your person record to attribute the upload');
      }
      
      const { data, error } = await supabase
        .from('Files')
        .insert(files.map(f => ({
          filename: f.filename,
          key: f.key,
          size: f.size,
          mimeType: f.mimeType,
          extension: f.extension,
          scope,
          ownerID,
          folderID: folderID === 'root' ? null : folderID,
          organizationID: orgID,
          uploadedByID: regUser.personID
        })))
        .select();
      
      if (error) {
        ErrorHandler(Error(error.message), { files, scope, ownerID, folderID }, 'Failed to create file records');
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.files.contents({ scope, ownerID, folderID })
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.files.storageUsage()
      });
    },
    onError: (_error) => {
      Toast.show({
        type: 'error',
        text2: 'Failed to save file records'
      });
    }
  });

  // Upload a single item
  const uploadItem = useCallback(async (item) => {
    setQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'uploading', progress: 0 } : i));
    
    try {
      let lastProgress = 0;
      const uploadResult = await uploadFromUri({
        uri: item.uri,
        file: item.file,
        key: item.key,
        contentType: item.mimeType,
        onProgress: (progress) => {
          const progressPercent = Math.round(progress * 100);
          if (progressPercent > lastProgress) {
            lastProgress = progressPercent;
            setQueue(prev => prev.map(i => i.id === item.id ? { ...i, progress: progressPercent } : i));
          }
        }
      });

      // uploadFromUri returns { error } on failure instead of throwing
      if (uploadResult?.error) {
        throw new Error(uploadResult.error?.message || 'Upload failed');
      }
      
      // Mark as done (file records are created by the completion effect below
      // once every item in the queue has settled)
      setQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'done', progress: 100 } : i));
    } catch (error) {
      setQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'error', error: error.message } : i));
      Toast.show({
        type: 'error',
        text2: `Failed to upload ${item.filename}: ${error.message}`
      });
    }
  }, []);

  // Start queued uploads as slots free up (bounded by MAX_PARALLEL_UPLOADS).
  const pumpUploads = useCallback(() => {
    while (activeUploadsRef.current < MAX_PARALLEL_UPLOADS && waitingUploadsRef.current.length > 0) {
      const next = waitingUploadsRef.current.shift();
      activeUploadsRef.current++;
      uploadItem(next)
        .catch(() => {})
        .finally(() => {
          activeUploadsRef.current--;
          pumpUploads();
        });
    }
  }, [uploadItem]);

  // When every item has settled (done or error), persist File records for the
  // done items, then clear them after a short delay so the user sees 100%.
  // Runs as an effect — not inside a setQueue updater — so React StrictMode
  // can't double-fire the insert and the timer is tracked for unmount cleanup.
  useEffect(() => {
    if (queue.length === 0) return;
    const allSettled = queue.every(i => i.status === 'done' || i.status === 'error');
    if (!allSettled) return;

    const doneItems = queue.filter(i => i.status === 'done' && !recordedIdsRef.current.has(i.id));
    if (doneItems.length === 0) return;
    doneItems.forEach(i => recordedIdsRef.current.add(i.id));

    createFileMutation.mutateAsync({ files: doneItems }).catch(() => {});

    clearTimeout(cleanupTimerRef.current);
    cleanupTimerRef.current = setTimeout(() => {
      setQueue(prev => prev.filter(i => i.status !== 'done'));
    }, 1000);
  }, [queue, createFileMutation]);

  useEffect(() => () => {
    clearTimeout(cleanupTimerRef.current);
  }, []);

  // Enqueue files for upload
  const enqueue = useCallback(async (items) => {
    const orgID = await getCurrentOrgID();

    // Block individual files over 100MB
    const MAX_FILE_BYTES = 100 * 1024 * 1024;
    const oversized = items.filter(item => (item.size || 0) > MAX_FILE_BYTES);
    oversized.forEach(item => {
      Toast.show({
        type: 'error',
        text1: 'File too large',
        text2: `${item.name || item.filename} exceeds the 100 MB per-file limit.`,
      });
    });
    const allowedItems = items.filter(item => (item.size || 0) <= MAX_FILE_BYTES);
    if (allowedItems.length === 0) return;

    // Check storage limit before proceeding
    const usage = await getOrgStorageUsage();
    const incomingBytes = allowedItems.reduce((sum, item) => sum + (item.size || 0), 0);
    const queuedBytes = queueRef.current
      .filter(i => i.status !== 'error' && i.status !== 'done')
      .reduce((sum, i) => sum + (i.size || 0), 0);

    if (usage.usedBytes + queuedBytes + incomingBytes > STORAGE_LIMIT_BYTES) {
      const remainingMB = Math.max(0, (STORAGE_LIMIT_BYTES - usage.usedBytes - queuedBytes) / (1024 * 1024));
      Toast.show({
        type: 'error',
        text1: 'Storage limit reached',
        text2: `Your organization has used its 10 GB storage limit. Only ${remainingMB.toFixed(0)} MB remaining.`,
      });
      return;
    }

    const existingFilenames = queueRef.current.filter(i => i.status !== 'error').map(i => i.filename);

    const newItems = await Promise.all(allowedItems.map(async (item) => {
      let filename = sanitizeFilename(item.name || item.filename);

      // Deduplicate filenames
      let counter = 1;
      let dedupedFilename = filename;
      while (existingFilenames.includes(dedupedFilename) || queueRef.current.some(q => q.filename === dedupedFilename)) {
        const match = filename.match(/(.+)(\.[^.]+)$/);
        const { base, ext } = match 
          ? { base: match[1], ext: match[2] }
          : { base: filename, ext: '' };
        dedupedFilename = `${base} (${counter})${ext}`;
        counter++;
      }
      
      // Compute storage key
      const scopePrefix = scope === 'resources' ? 'resources' : scope === 'event' ? `events/${ownerID}` : `people/${ownerID}`;
      const key = `${orgID}/${scopePrefix}/${folderID === 'root' ? '' : ''}${dedupedFilename}`;
      
      return {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        uri: item.uri,
        file: item.file || null,
        filename: dedupedFilename,
        originalName: item.name || item.filename,
        size: item.size || 0,
        mimeType: item.mimeType,
        extension: dedupedFilename.includes('.') ? dedupedFilename.substring(dedupedFilename.lastIndexOf('.')).toLowerCase() : null,
        key,
        progress: 0,
        status: 'queued',
        error: null
      };
    }));
    
    setQueue(prev => [...prev, ...newItems]);

    // Upload up to MAX_PARALLEL_UPLOADS at a time; the rest start as slots free.
    waitingUploadsRef.current.push(...newItems);
    pumpUploads();
  }, [scope, ownerID, folderID, pumpUploads]);

  // Retry a failed upload
  const retry = useCallback((itemId) => {
    const item = queueRef.current.find(i => i.id === itemId);
    if (item && !waitingUploadsRef.current.some(i => i.id === itemId)) {
      setQueue(prev => prev.map(i => i.id === itemId ? { ...i, status: 'queued', error: null } : i));
      waitingUploadsRef.current.push(item);
      pumpUploads();
    }
  }, [pumpUploads]);

  // Cancel/remove an item from queue
  const cancel = useCallback((itemId) => {
    waitingUploadsRef.current = waitingUploadsRef.current.filter(i => i.id !== itemId);
    setQueue(prev => prev.filter(i => i.id !== itemId));
  }, []);

  // Calculate total progress
  const totalProgress = queue.length > 0 
    ? queue.reduce((sum, item) => sum + item.progress, 0) / queue.length 
    : 0;

  const isUploading = queue.some(item => item.status === 'uploading' || item.status === 'queued');
  const hasErrors = queue.some(item => item.status === 'error');

  return {
    queue,
    enqueue,
    retry,
    cancel,
    totalProgress,
    isUploading,
    hasErrors,
    clearAll: useCallback(() => {
      waitingUploadsRef.current = [];
      setQueue([]);
    }, [])
  };
};
